import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { Comment } from './comment.js';

export enum ContactStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  LEAD = 'LEAD',
  CUSTOMER = 'CUSTOMER',
  PROSPECT = 'PROSPECT',
}

registerEnum({ enum: ContactStatus, name: 'ContactStatus' });

@Entity()
export class ContactEntity extends MongoEntity {
  static override _class = 'ContactEntity';
  static override _collectionName = 'contacts';

  @Field(() => Date) timestamp: Maybe<Date>;
  @Field(() => String) companyName: Maybe<string | null | undefined>;
  @Field(() => String) companyAction: Maybe<string>;
  @Field(() => String) companyUpdates: Maybe<string>;
  @Field(() => String) personAction: Maybe<string>;
  @Field(() => String) personUpdates: Maybe<string>;
  @Field(() => String) email: Maybe<string | null | undefined>;
  @Field(() => String) phone: Maybe<string | null | undefined>;
  @Field(() => String) position: Maybe<string | null | undefined>;
  @Field(() => String) location: Maybe<string | null | undefined>;
  @Field(() => ContactStatus) status: Maybe<ContactStatus>;
  @Field(() => [Comment]) notes: Maybe<Comment[]>;
  @Field(() => String) odooTicketID: Maybe<string>;
  @Field(() => Boolean) llmSuccess: Maybe<boolean>;
  @Field(() => String) llmErrorNote: Maybe<string>;
  @Field(() => Boolean) isCheckedBySupport: Maybe<boolean>;
  @Field(() => Boolean) internalContact: Maybe<boolean>;

  static override async beforeCreate(entity: ContactEntity) {
    await super.beforeCreate(entity);
    entity.timestamp = new Date();
  }
}

export async function checkIfContactExists(ticketId: string): Promise<ContactEntity | null> {
  const collection = await getRawCollection<EntityData<ContactEntity>>(ContactEntity._collectionName);
  const contactEntity = await collection.findOne({ odooTicketID: ticketId });
  return contactEntity ? new ContactEntity(contactEntity) : null;
}

export async function setContactLlmSuccessOrError(
  ticketId: string,
  llmSuccess: boolean,
  errorNote: string | null,
): Promise<void> {
  const collection = await getCollection<ContactEntity>(ContactEntity._class);
  await collection.updateOne({ odooTicketID: ticketId }, { $set: { llmSuccess: llmSuccess } });
  if (!llmSuccess && errorNote) {
    await collection.updateOne({ odooTicketID: ticketId }, { $set: { llmErrorNote: errorNote } });
  }
}

export async function odooTicketIdsAsNumberFromContactCollection(newerThan: Date): Promise<number[]> {
  const contactCollection = await getRawCollection<EntityData<ContactEntity>>(ContactEntity._collectionName);
  const contacts = await contactCollection
    .find(
      { odooTicketID: { $exists: true, $ne: null }, timestamp: { $gte: newerThan } },
      { projection: { odooTicketID: 1 } },
    )
    .toArray();
  return contacts
    .map(contact => contact.odooTicketID)
    .filter((id): id is string => id !== null && id !== undefined)
    .map(id => parseInt(id));
}

export async function setContactAsInternal(contactId: string): Promise<void> {
  const collection = await getCollection<ContactEntity>(ContactEntity._class);
  await collection.updateOne({ _id: contactId }, { $set: { internalContact: true } });
}

export async function deleteContactEntityByOdooTicketId(ticketId: string): Promise<any> {
  const collection = await getCollection<ContactEntity>(ContactEntity._class);
  return await collection.deleteOne({ odooTicketID: ticketId });
}
